import { Box } from '@chakra-ui/react';
import { useHeader } from 'hooks/useHeader';
import type { ChangeEvent, KeyboardEvent } from 'react';
import { useEffect, useRef, useState } from 'react';

import { SearchHeader } from './SearchHeader';
import { SearchPortal } from './SearchPortal';

export function SiteSearchMobile() {
  const searchHeaderRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [headerTopPos, setHeaderTopPos] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const setIsSearchOpen = useHeader((state) => state.setIsSearchOpen);

  useEffect(() => {
    setIsSearchOpen(isActive);

    return () => setIsSearchOpen(false);
  }, [isActive, setIsSearchOpen]);

  const inputProps = {
    placeholder: 'Search',
    value: searchTerm,
    onChange: (e: ChangeEvent<HTMLInputElement>) =>
      setSearchTerm(e.target.value),
  };

  // Deliberately not onFocus: focus happens on Tab and whenever focus is
  // restored, so opening from it makes the field impossible to pass or
  // return to without the overlay reappearing.
  const openSearch = () => {
    if (!searchHeaderRef.current) return;

    const { top } = searchHeaderRef.current.getBoundingClientRect();

    setHeaderTopPos(top);
    setIsActive(true);
  };

  const handleNavigate = () => {
    setSearchTerm('');
  };

  return (
    <>
      {/* Hidden by opacity alone, so without this the portal's input is
       * shadowed by an invisible duplicate in the tab order. */}
      <Box
        ref={searchHeaderRef}
        opacity={isActive ? 0 : 1}
        inert={isActive || undefined}
      >
        <SearchHeader
          inputProps={{
            ...inputProps,
            isReadOnly: true,
            onClick: openSearch,
            onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              openSearch();
            },
          }}
          isActive={false}
        />
      </Box>
      {isActive && (
        <SearchPortal
          isActive
          headerTopPos={headerTopPos}
          setIsActive={setIsActive}
          inputProps={inputProps}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          handleNavigate={handleNavigate}
        />
      )}
    </>
  );
}
